import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Grid,
    Typography,
    Button,
    ButtonGroup,
    Alert,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
    startOfDay,
    endOfDay,
    startOfWeek,
    endOfWeek,
    startOfMonth,
    endOfMonth,
    startOfYear,
    endOfYear,
    differenceInYears,
    isValid,
    format
} from 'date-fns';
import { Search, Assessment, Spa, Inventory, AttachMoney } from '@mui/icons-material';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer
} from 'recharts';
import FeatureGuard from '../common/FeatureGuard';
import apiService from '../../services/api';
import { useCurrency } from '../../contexts/CurrencyContext';
import { StatCard, Loading } from '../common';

const SalesByServiceAndProducts = () => {
    const [filterType, setFilterType] = useState('month');
    const [dateRange, setDateRange] = useState({
        from: startOfMonth(new Date()),
        to: endOfDay(new Date())
    });
    const [customRange, setCustomRange] = useState({
        from: startOfMonth(new Date()),
        to: endOfDay(new Date())
    });
    const [error, setError] = useState('');
    const [salesData, setSalesData] = useState([]);
    const [loading, setLoading] = useState(false);
    const { formatPrice } = useCurrency();

    // Initial Fetch
    useEffect(() => {
        fetchSalesData();
    }, []);

    const fetchSalesData = async () => {
        setLoading(true);
        try {
            const startDate = format(dateRange.from, 'yyyy-MM-dd');
            const endDate = format(dateRange.to, 'yyyy-MM-dd');

            // Fetch service and product sales from dedicated API endpoint
            const response = await apiService.get(`/reports/service-products?startDate=${startDate}&endDate=${endDate}`);
            if (response.success) {
                const salesData = response.data.salesData || [];
                setSalesData(salesData);
            } else {
                setError('Failed to fetch sales data');
            }
        } catch (err) {
            setError('Error fetching data: ' + err.message);
            console.error('Error fetching sales data:', err);
        } finally {
            setLoading(false);
        }
    };

    // Handle filter buttons
    const handleFilterChange = (type) => {
        setFilterType(type);
        setError('');

        const now = new Date();
        let newRange = { from: now, to: now };

        switch (type) {
            case 'today':
                newRange = { from: startOfDay(now), to: endOfDay(now) };
                break;
            case 'week':
                newRange = { from: startOfWeek(now, { weekStartsOn: 1 }), to: endOfDay(now) };
                break;
            case 'month':
                newRange = { from: startOfMonth(now), to: endOfDay(now) };
                break;
            case 'year':
                newRange = { from: startOfYear(now), to: endOfDay(now) };
                break;
            case 'custom':
                newRange = customRange;
                break;
            default:
                break;
        }

        setDateRange(newRange);
    };

    // Effect to refetch when dateRange changes
    useEffect(() => {
        if (filterType !== 'custom') {
            fetchSalesData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dateRange, filterType]);

    // Handle custom date changes
    const handleCustomDateChange = (prop, value) => {
        if (!isValid(value)) return;

        let adjustedValue = value;
        if (prop === 'to') {
            adjustedValue = endOfDay(value);
        } else if (prop === 'from') {
            adjustedValue = startOfDay(value);
        }

        const newCustomRange = { ...customRange, [prop]: adjustedValue };
        setCustomRange(newCustomRange);

        if (newCustomRange.from && newCustomRange.to) {
            if (newCustomRange.from > newCustomRange.to) {
                setError('Start date cannot be after end date');
                return;
            }

            const diff = differenceInYears(newCustomRange.to, newCustomRange.from);
            if (diff >= 1) {
                setError('Date range cannot exceed 1 year');
            } else {
                setError('');
                setDateRange(newCustomRange);
            }
        }
    };

    // Calculate Summaries
    const totalServiceRevenue = salesData.reduce((sum, item) => sum + item.serviceRevenue, 0);
    const totalProductRevenue = salesData.reduce((sum, item) => sum + item.productRevenue, 0);
    const totalRevenue = totalServiceRevenue + totalProductRevenue;
    const totalServiceCount = salesData.reduce((sum, item) => sum + item.serviceCount, 0);
    const totalProductCount = salesData.reduce((sum, item) => sum + item.productCount, 0);

    if (loading) return <Loading message="Loading sales by service and products..." />;

    return (
        <FeatureGuard feature="Reports & Analytics">
            <Box p={3}>
                {/* Header */}
                <Box display="flex" alignItems="center" mb={3}>
                    <Assessment sx={{ fontSize: 40, mr: 2, color: 'primary.main' }} />
                    <Box>
                        <Typography variant="h4" fontWeight="bold">
                            Sales by Service and Products
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Performance breakdown by services and products
                        </Typography>
                    </Box>
                </Box>

                {/* Filters Card */}
                <Card sx={{ mb: 4 }}>
                    <CardContent>
                        <Grid container spacing={3} alignItems="center">
                            <Grid item xs={12} md={7}>
                                <Typography variant="subtitle2" gutterBottom>
                                    Time Period
                                </Typography>
                                <ButtonGroup variant="outlined" aria-label="time period filters">
                                    <Button
                                        onClick={() => handleFilterChange('today')}
                                        variant={filterType === 'today' ? 'contained' : 'outlined'}
                                    >
                                        Today
                                    </Button>
                                    <Button
                                        onClick={() => handleFilterChange('week')}
                                        variant={filterType === 'week' ? 'contained' : 'outlined'}
                                    >
                                        This Week
                                    </Button>
                                    <Button
                                        onClick={() => handleFilterChange('month')}
                                        variant={filterType === 'month' ? 'contained' : 'outlined'}
                                    >
                                        This Month
                                    </Button>
                                    <Button
                                        onClick={() => handleFilterChange('year')}
                                        variant={filterType === 'year' ? 'contained' : 'outlined'}
                                    >
                                        This Year
                                    </Button>
                                    <Button
                                        onClick={() => handleFilterChange('custom')}
                                        variant={filterType === 'custom' ? 'contained' : 'outlined'}
                                    >
                                        Custom
                                    </Button>
                                </ButtonGroup>
                            </Grid>

                            {filterType === 'custom' && (
                                <Grid item xs={12} md={5}>
                                    <Typography variant="subtitle2" gutterBottom>
                                        Custom Range
                                    </Typography>
                                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                                        <Box display="flex" gap={2} alignItems="flex-start">
                                            <DatePicker
                                                label="From"
                                                value={customRange.from}
                                                onChange={(newValue) => handleCustomDateChange('from', newValue)}
                                                slotProps={{ textField: { size: 'small', fullWidth: true } }}
                                            />
                                            <DatePicker
                                                label="To"
                                                value={customRange.to}
                                                onChange={(newValue) => handleCustomDateChange('to', newValue)}
                                                slotProps={{ textField: { size: 'small', fullWidth: true } }}
                                            />
                                        </Box>
                                    </LocalizationProvider>
                                </Grid>
                            )}
                        </Grid>

                        {error && (
                            <Alert severity="error" sx={{ mt: 2 }}>
                                {error}
                            </Alert>
                        )}

                        <Divider sx={{ my: 2 }} />

                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2" color="text.secondary">
                                Reporting Period: <strong>{format(dateRange.from, 'PP')}</strong> - <strong>{format(dateRange.to, 'PP')}</strong>
                            </Typography>
                            <Button variant="outlined" startIcon={<Search />} onClick={fetchSalesData} size="small">
                                Refresh Data
                            </Button>
                        </Box>
                    </CardContent>
                </Card>

                {/* Summary Cards */}
                <Grid container spacing={3} mb={4}>
                    <Grid item xs={12} md={4}>
                        <StatCard
                            title="Total Service Revenue"
                            value={formatPrice(totalServiceRevenue)}
                            icon={<Spa />}
                            color="primary"
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <StatCard
                            title="Total Product Revenue"
                            value={formatPrice(totalProductRevenue)}
                            icon={<Inventory />}
                            color="info"
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <StatCard
                            title="Total Revenue"
                            value={formatPrice(totalRevenue)}
                            icon={<AttachMoney />}
                            color="success"
                        />
                    </Grid>
                </Grid>

                {/* Revenue Chart - Vertical with Services and Products side by side */}
                {salesData.length > 0 && (
                    <Card sx={{ mb: 4 }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Revenue by Service and Products
                            </Typography>
                            <ResponsiveContainer width="100%" height={300}>
                                <BarChart
                                    data={salesData}
                                    margin={{
                                        top: 20,
                                        right: 30,
                                        left: 20,
                                        bottom: 60,
                                    }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis
                                        dataKey="name"
                                        angle={-45}
                                        textAnchor="end"
                                        height={100}
                                        interval={0}
                                    />
                                    <YAxis
                                        tickFormatter={(value) => formatPrice(value)}
                                    />
                                    <Tooltip
                                        formatter={(value) => formatPrice(value)}
                                        labelStyle={{ color: '#000' }}
                                    />
                                    <Legend />
                                    <Bar
                                        dataKey="serviceRevenue"
                                        fill="#1976d2"
                                        name="Services"
                                        radius={[8, 8, 0, 0]}
                                        maxBarSize={60}
                                    />
                                    <Bar
                                        dataKey="productRevenue"
                                        fill="#00C49F"
                                        name="Products"
                                        radius={[8, 8, 0, 0]}
                                        maxBarSize={60}
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                        </CardContent>
                    </Card>
                )}

                {/* Data Table */}
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell align="center">Service Revenue</TableCell>
                                <TableCell align="center">Product Revenue</TableCell>
                                <TableCell align="center">Service Count</TableCell>
                                <TableCell align="center">Product Count</TableCell>
                                <TableCell align="right">Total Revenue</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {salesData.length > 0 ? (
                                salesData.map((item, index) => (
                                    <TableRow key={index}>
                                        <TableCell>
                                            <Typography fontWeight="medium">
                                                {item.name}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            {item.serviceRevenue > 0 ? formatPrice(item.serviceRevenue) : '-'}
                                        </TableCell>
                                        <TableCell align="center">
                                            {item.productRevenue > 0 ? formatPrice(item.productRevenue) : '-'}
                                        </TableCell>
                                        <TableCell align="center">{item.serviceCount || '-'}</TableCell>
                                        <TableCell align="center">{item.productCount || '-'}</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                            {formatPrice(item.serviceRevenue + item.productRevenue)}
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={6} align="center">
                                        <Box py={3}>
                                            <Typography color="text.secondary">
                                                No sales data found for this period.
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

            </Box>
        </FeatureGuard>
    );
};

export default SalesByServiceAndProducts;

